import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiRequest } from '../../services/api/axiosConfig';

// ユーザー詳細情報の型定義
interface UserProfile {
  id: number;
  name: string;
  email: string;
  avatar: string | null;
  subscription_level: string;
  created_at: string;
  updated_at: string;
}

// 使用統計情報の型定義
interface UsageStatistics {
  api_calls: number;
  date: string;
  limit: number;
  remaining: number;
}

// ユーザー設定の型定義
interface UserSettings {
  theme: 'light' | 'dark' | 'system';
  notifications_enabled: boolean;
  language: string;
}

// ユーザー状態の型定義
interface UserState {
  profile: UserProfile | null;
  usageStatistics: UsageStatistics | null;
  settings: UserSettings;
  isLoading: boolean;
  error: string | null;
}

// 初期状態
const initialState: UserState = {
  profile: null,
  usageStatistics: null,
  settings: {
    theme: 'system',
    notifications_enabled: true,
    language: 'ja'
  },
  isLoading: false,
  error: null
};

// ユーザープロフィール更新非同期アクション
export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (userData: { name?: string; email?: string; avatar?: File }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      // FormDataを使用して画像アップロードをサポート
      const formData = new FormData();
      if (userData.name) formData.append('name', userData.name);
      if (userData.email) formData.append('email', userData.email);
      if (userData.avatar) formData.append('avatar', userData.avatar);

      const response = await fetch('/api/user/profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Content-TypeはformDataを使用する場合は自動設定
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'プロフィール更新に失敗しました');
      }

      return data.data.user;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// 購読プラン変更非同期アクション
export const changeSubscription = createAsyncThunk(
  'user/changeSubscription',
  async (subscriptionLevel: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const response = await fetch('/api/user/subscription', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription_level: subscriptionLevel })
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || '購読プラン変更に失敗しました');
      }

      return data.data.user;
    } catch (error) {
      return rejectWithValue('サーバーとの通信に失敗しました');
    }
  }
);

// APIレスポンスの型定義
interface UsageStatisticsResponse {
  data: {
    usage: UsageStatistics;
  };
}

// 使用統計取得非同期アクション
export const fetchUsageStatistics = createAsyncThunk(
  'user/fetchUsageStatistics',
  async (_, { rejectWithValue }) => {
    try {
      // apiRequestを使用して使用統計情報を取得（型パラメータを指定）
      const response = await apiRequest.get<UsageStatisticsResponse>('/users/usage-statistics');
      
      // レスポンスデータからusage情報を抽出
      return response.data?.usage;
    } catch (error) {
      return rejectWithValue('使用統計の取得に失敗しました');
    }
  }
);

// ユーザースライス
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },
    updateSettings: (state, action: PayloadAction<Partial<UserSettings>>) => {
      state.settings = { ...state.settings, ...action.payload };
      // ローカルストレージに設定を保存
      localStorage.setItem('userSettings', JSON.stringify(state.settings));
    },
    loadSettings: (state) => {
      const savedSettings = localStorage.getItem('userSettings');
      if (savedSettings) {
        state.settings = { ...state.settings, ...JSON.parse(savedSettings) };
      }
    }
  },
  extraReducers: (builder) => {
    // ユーザープロフィール更新処理
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 購読プラン変更処理
    builder
      .addCase(changeSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changeSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(changeSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 使用統計取得処理
    builder
      .addCase(fetchUsageStatistics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsageStatistics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.usageStatistics = action.payload;
        state.error = null;
      })
      .addCase(fetchUsageStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearUserError, updateSettings, loadSettings } = userSlice.actions;

export default userSlice.reducer;
